import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Table, Button, message, Spin, Alert } from 'antd';
import { UserOutlined, DatabaseOutlined, FileTextOutlined, GlobalOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
// Removed unused imports: formatVietnamTime, formatVietnamDate, ensureArray
import axios from 'axios';

function Dashboard() {
  const { user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUIDs: 0,
    totalRequests: 0,
    activeProxies: 0
  });
  const [recentRequests, setRecentRequests] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (user.is_admin) {
        // Admin dashboard - show system overview
        const [usersRes, uidsRes, requestsRes, proxiesRes] = await Promise.all([
          axios.get('/api/users/'),
          axios.get('/api/uids/'),
          axios.get('/api/text-to-voice/'),
          axios.get('/api/proxies/')
        ]);
        
        // Handle paginated responses
        const usersData = usersRes.data?.data || usersRes.data || [];
        const uidsData = uidsRes.data?.data || uidsRes.data || [];
        const requestsData = requestsRes.data?.data || requestsRes.data || [];
        const proxiesData = proxiesRes.data?.data || proxiesRes.data || [];

        console.log('📊 Dashboard API responses:', {
          users: usersData.length,
          uids: uidsData.length,
          requests: requestsData.length,
          proxies: proxiesData.length
        });

        setStats({
          totalUsers: usersData.length,
          activeUIDs: uidsData.filter(u => u.is_active).length,
          totalRequests: requestsData.length,
          activeProxies: proxiesData.filter(p => p.is_active).length
        });

        // Get recent requests
        const recentRequestsData = requestsData
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);
        
        setRecentRequests(recentRequestsData);
      } else {
        // User dashboard - show only user's data
        const requestsRes = await axios.get('/api/text-to-voice/');
        
        // Handle paginated response
        const requestsData = requestsRes.data?.data || requestsRes.data || [];
        
        setStats({
          totalUsers: 0,
          activeUIDs: 0,
          totalRequests: requestsData.length,
          activeProxies: 0
        });

        // Get user's recent requests
        const recentRequestsData = requestsData
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);
        
        setRecentRequests(recentRequestsData);
      }
      
    } catch (error) {
      console.error('❌ Dashboard error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      message.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted && user) {
        await fetchDashboardData();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [user, fetchDashboardData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#52c41a';
      case 'processing': return '#1890ff';
      case 'failed': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      key: 'stt',
    },
    {
      title: 'Text',
      dataIndex: 'text',
      key: 'text',
      ellipsis: true,
      render: (text) => text?.substring(0, 50) + (text?.length > 50 ? '...' : ''),
    },
    {
      title: 'Chars',
      dataIndex: 'chars',
      key: 'chars',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{ color: getStatusColor(status) }}>
          {status.toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Voice',
      dataIndex: 'voice_code',
      key: 'voice_code',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>
          {user.is_admin 
            ? 'Voice Synthesis Management System Overview' 
            : 'Your Voice Synthesis Dashboard'
          }
        </p>
      </div>
      
      {/* Warning for users with no character limit */}
      {!user.is_admin && user.char_limit === 0 && (
        <Alert
          message="No Character Quota"
          description="You have no character limit. Please contact admin to get character quota for text-to-voice synthesis."
          type="warning"
          icon={<ExclamationCircleOutlined />}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Plan expiry warning */}
      {!user.is_admin && (() => {
        const expiresAt = user.plan_expires_at;
        if (!expiresAt) return null;
        const expiryDate = new Date(expiresAt);
        const now = Date.now();
        const diffMs = expiryDate.getTime() - now;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (diffMs <= 0) {
          return (
            <Alert
              type="error"
              showIcon
              message="Service Plan Expired"
              description={
                <span>
                  Your account expired on <strong>{expiryDate.toLocaleDateString()}</strong>.
                  Voice synthesis has been suspended.{' '}
                  <a href="/pricing">Renew now →</a>
                </span>
              }
              style={{ marginBottom: 16 }}
            />
          );
        }

        if (diffDays <= 3) {
          return (
            <Alert
              type="warning"
              showIcon
              message={`Service plan expiring soon (${diffDays} day(s) left)`}
              description={
                <span>
                  Your plan will expire on <strong>{expiryDate.toLocaleDateString()}</strong>.{' '}
                  <a href="/pricing">Renew to avoid interruption →</a>
                </span>
              }
              style={{ marginBottom: 16 }}
            />
          );
        }

        return null;
      })()}

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {user.is_admin ? (
          <>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Users"
                  value={stats.totalUsers}
                  prefix={<UserOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Active UIDs"
                  value={stats.activeUIDs}
                  prefix={<DatabaseOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Total Requests"
                  value={stats.totalRequests}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="stat-card">
                <Statistic
                  title="Active Proxies"
                  value={stats.activeProxies}
                  prefix={<GlobalOutlined />}
                />
              </Card>
            </Col>
          </>
        ) : (
          <>
            <Col xs={24} sm={12} lg={8}>
              <Card className="stat-card">
                <Statistic
                  title="My Requests"
                  value={stats.totalRequests}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card className="stat-card">
                {(() => {
                  const limit = Number(user.char_limit || 0);
                  const isUnlimited = limit >= 1e15;
                  return (
                    <Statistic
                      title="Character Limit"
                      value={isUnlimited ? 'Unlimited' : limit}
                      prefix={<UserOutlined />}
                      valueStyle={{
                        fontSize: isUnlimited ? '24px' : undefined,
                        wordBreak: 'break-all',
                      }}
                      groupSeparator=","
                    />
                  );
                })()}
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card className="stat-card">
                <Statistic
                  title="Used Characters"
                  value={user.used_chars || 0}
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ wordBreak: 'break-all' }}
                  groupSeparator=","
                />
              </Card>
            </Col>
          </>
        )}
      </Row>

      <Card 
        title={user.is_admin ? "Recent Text Requests" : "My Recent Requests"} 
        extra={
          <Button onClick={fetchDashboardData} size="small">
            Refresh
          </Button>
        }
        className="recent-requests-card"
      >
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={recentRequests || []}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 600 }}
          />
        </div>
      </Card>
    </div>
  );
}

export default Dashboard;